import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#050505] text-white">
      <div className="hidden md:flex flex-col justify-between p-12 relative overflow-hidden border-r border-ink-600">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="absolute inset-0 beam pointer-events-none" />
        <Link to="/" className="relative inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white z-10">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6 group">
            <span className="relative flex items-center justify-center w-10 h-10 border border-white/20 group-hover:border-[#00F0FF] transition-colors">
              <span className="absolute inset-1 bg-[#00F0FF] shadow-[0_0_18px_2px_rgba(0,240,255,0.55)]" />
              <span className="absolute -right-1 -bottom-1 w-2 h-2 bg-[#00F0FF] pulse-dot" />
            </span>
          </div>
          <div className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-white">
            HUMINT<span className="text-cy">.</span>
            <span className="block text-zinc-500 text-3xl lg:text-4xl">/ admin</span>
          </div>
          <p className="mt-6 text-zinc-400 max-w-sm">
            Operating console for the HumInt advisory practice. Leads, signals, cadence.
          </p>
        </div>
        <div className="relative font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500 z-10">
          Diagnose · Discover · Deliver
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-cy mb-3">
            humint.admin / login
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white mb-8">
            Sign in.
          </h1>
          <label className="block font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email"
            className="w-full bg-transparent border border-ink-500 focus:border-cy focus:outline-none px-4 py-3 text-sm text-white mb-5"
          />
          <label className="block font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">
            Password
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
            className="w-full bg-transparent border border-ink-500 focus:border-cy focus:outline-none px-4 py-3 text-sm text-white mb-8"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-cy text-ink px-6 py-3 text-sm font-semibold hover:bg-white disabled:opacity-60 transition-colors"
          >
            {submitting ? "Signing in…" : "Enter console"}
            <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
